import { useState } from 'react'
import { json, redirect } from '@remix-run/node'
import { Link, useActionData, useLoaderData } from '@remix-run/react'
import { Switch } from '@headlessui/react'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationIcon,
} from '@heroicons/react/solid'
import { PRIVACY_FILE, TERMS_FILE } from '~/components/box'
import { inviteMember } from '~/models/circle/members.server'
import { getTenant } from '~/models/tenant.server'
import {
  createInvitation,
  getUserByEmailAndConnection,
} from '~/services/auth0.m2m.server'

import type { ActionFunction, LoaderFunction } from 'remix'
import type { Tenant } from '~/models/tenant.server'

/**
 * Loader
 */
type LoaderData = Tenant

export const loader: LoaderFunction = async ({ request }) => {
  /**
   * If tenant does not support self invite, redirect /.
   */
  const tenant: Tenant = getTenant(request)
  if (!tenant.selfInvite) return redirect('/')

  /**
   * If logged in, redirect to /.
   */
  await tenant.authenticator.isAuthenticated(request, {
    successRedirect: '/',
  })

  return tenant
}

/**
 * Action
 */
const badRequest = (data: ActionData) => json(data, { status: 400 })

function validateConsent(consent: string) {
  if (!consent || consent !== 'true') {
    return 'Consent is required.'
  }
}
function validateEmail(email: string, tenant: Tenant) {
  if (!email) {
    return 'Email required.'
  }

  const [username, domain] = email.split('@')
  if (!username || !domain) {
    return 'Email invalid.'
  }

  // TODO: refactor as this is specific to TRGroup
  const allowlist = tenant.selfInvite?.allowlist
  if (!allowlist || !allowlist.includes(domain)) {
    return 'We do not recognize that email address. Please be sure to use your business email address, and that it is associated with a licensed bank or credit union. If you need assistance, please contact: support@bankabledomain.com.'
  }
}

export let action: ActionFunction = async ({ request }) => {
  /**
   * If tenant does not support self invite, redirect /.
   */
  const tenant: Tenant = getTenant(request)
  if (!tenant.selfInvite) return redirect('/')

  /**
   * If logged in, redirect to /.
   */
  await tenant.authenticator.isAuthenticated(request, {
    successRedirect: '/',
  })

  /**
   * Form validation
   */
  const form = await request.formData()
  const firstname = form.get('firstname')
  const lastname = form.get('lastname')
  const name = firstname + ' ' + lastname
  const company = form.get('company')
  const title = form.get('title')
  const email = form.get('email')
  const consent = form.get('consent')
  const fieldErrors = {
    firstname: null,
    lastname: null,
    company: null,
    title: null,
    email: validateEmail(email, tenant),
    consent: validateConsent(consent),
  }
  const fields = { firstname, lastname, company, title, email, consent }
  if (Object.values(fieldErrors).some(Boolean)) {
    return badRequest({ fieldErrors, fields })
  }

  /**
   * Check if Auth0 user.
   */
  try {
    const existingUser = await getUserByEmailAndConnection(
      email,
      tenant.connection
    )
    if (existingUser.length >= 1) {
      return json({
        formAlert: {
          success: true,
          icon: CheckCircleIcon,
          title: 'Welcome back!',
          message: 'Please login. Need help? Email support@bankabledomain.com.',
        },
        fields,
      })
    }
  } catch (error) {
    return badRequest({
      formAlert: {
        error: true,
        icon: ExclamationIcon,
        title: 'Technical Difficulty',
        message:
          "Oops! We're experiencing technical difficulties. Please retry your request. If the issue persists, please contact support@bankabledomain.com.",
      },
      fields,
    })
  }

  /**
   * Send Circle invite.
   * Perform Circle operation first. This operation disables email notification.
   * If API/network failure, this operation blocks Auth0 invitation.
   * This is a temporary order of operations shim while a more resiliant solution is implemented.
   */
  try {
    var headline = ''
    if (company && title) headline = `${title} at ${company}`
    await inviteMember(tenant.id, email, name, headline)
  } catch (error) {
    return badRequest({
      formAlert: {
        error: true,
        icon: ExclamationIcon,
        title: 'Technical Difficulty',
        message:
          "Oops! We're experiencing technical difficulties. Please retry your request. If the issue persists, please contact support@bankabledomain.com.",
      },
      fields,
    })
  }

  /**
   * Send Auth0 invite.
   */
  try {
    await createInvitation(tenant, email, {
      given_name: firstname,
      family_name: lastname,
      name: name,
      company: company,
      title: title,
      consents: [
        {
          given: true,
          date: new Date().toJSON(),
          terms_url: TERMS_FILE,
          privacy_url: PRIVACY_FILE,
        },
      ],
    })
    return json({
      formAlert: {
        success: true,
        icon: CheckCircleIcon,
        title: "You're almost there!",
        message:
          "We've sent an email verification. Please check your email account for it. The sender is support@bankabledomain.com. P.S. Remember to look in your spam folders, as well. Need help? Email support@bankabledomain.com.",
      },
      fields,
    })
  } catch (error) {
    return badRequest({
      formAlert: {
        error: true,
        icon: ExclamationIcon,
        title: 'Technical Difficulty',
        message:
          "Oops! We're experiencing technical difficulties. Please retry your request. If the issue persists, please contact support@bankabledomain.com.",
      },
      fields,
    })
  }
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function Consent() {
  const [agreed, setAgreed] = useState(false)
  const actionData = useActionData<ActionData>()
  const tenant = useLoaderData<LoaderData>()

  function isTenantTRGroup() {
    return tenant.orgId === 'org_Ew0fMl5VrOWWwxzH'
  }

  return (
    <div className="overflow-hidden bg-white py-16 px-4 sm:px-6 lg:px-8 lg:py-24">
      <div className="relative mx-auto max-w-2xl">
        <svg
          className="absolute left-full translate-x-1/2 transform"
          width={404}
          height={404}
          fill="none"
          viewBox="0 0 404 404"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="85737c0e-0916-41d7-917f-596dc7edfa27"
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <rect
                x={0}
                y={0}
                width={4}
                height={4}
                className="text-gray-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            width={404}
            height={404}
            fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
          />
        </svg>
        <svg
          className="absolute right-full bottom-0 -translate-x-1/2 transform"
          width={404}
          height={404}
          fill="none"
          viewBox="0 0 404 404"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="85737c0e-0916-41d7-917f-596dc7edfa27"
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <rect
                x={0}
                y={0}
                width={4}
                height={4}
                className="text-gray-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            width={404}
            height={404}
            fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
          />
        </svg>
        <div className="text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Welcome!
          </h2>
          <p className="mt-4 text-lg leading-6 text-gray-500">
            {isTenantTRGroup() && (
              <p className="mt-2">
                If you are a member of{' '}
                <a
                  className={classNames(
                    tenant.color == 'teal'
                      ? 'text-teal-700 hover:text-teal-600'
                      : 'text-blue-700 hover:text-blue-600',
                    'font-medium underline'
                  )}
                  href={tenant.orgUrl}
                >
                  {tenant.name}
                </a>
                , then you are in the right place to get free access to your
                private community of credit union peers.
              </p>
            )}
            <p className="mt-2">
              Through Bankable Fintech, you will also have access to bank and
              credit union peers, at your discretion.
            </p>
            <p className="mt-2">
              Please complete this form and press "Submit" to create an account.
            </p>
          </p>
        </div>
        <>
          {actionData?.formAlert ? (
            <div
              className={classNames(
                actionData.formAlert.error ? 'bg-yellow-50' : 'bg-green-50',
                'mt-12 rounded-md p-4'
              )}
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  {actionData.formAlert.error ? (
                    <ExclamationIcon
                      className="h-5 w-5 text-yellow-400"
                      aria-hidden="true"
                    />
                  ) : (
                    <CheckCircleIcon
                      className="h-5 w-5 text-green-400"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="ml-3">
                  <h3
                    className={classNames(
                      actionData.formAlert.error
                        ? 'text-yellow-800'
                        : 'text-green-800',
                      'text-sm font-medium'
                    )}
                  >
                    {actionData.formAlert.title}
                  </h3>
                  <div
                    className={classNames(
                      actionData.formAlert.error
                        ? 'text-yellow-700'
                        : 'text-green-700',
                      'mt-2 text-sm'
                    )}
                  >
                    <p>{actionData.formAlert.message}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </>
        <div className="mt-12">
          <form
            action="#"
            method="POST"
            className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
          >
            <div>
              <label
                htmlFor="firstname"
                className="block text-sm font-medium text-gray-700"
              >
                First name *
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="firstname"
                  id="firstname"
                  defaultValue={actionData?.fields?.firstname}
                  autoComplete="given-name"
                  className="block w-full rounded-md border-gray-300 py-3 px-4 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="lastname"
                className="block text-sm font-medium text-gray-700"
              >
                Last name *
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="lastname"
                  id="lastname"
                  defaultValue={actionData?.fields?.lastname}
                  autoComplete="family-name"
                  className="block w-full rounded-md border-gray-300 py-3 px-4 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div className="sm:col-span-1">
              <label
                htmlFor="company"
                className="block text-sm font-medium text-gray-700"
              >
                Company Name *
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="company"
                  id="company"
                  defaultValue={actionData?.fields?.company}
                  autoComplete="organization"
                  className="block w-full rounded-md border-gray-300 py-3 px-4 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div className="sm:col-span-1">
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700"
              >
                Title *
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="title"
                  id="title"
                  defaultValue={actionData?.fields?.title}
                  className="block w-full rounded-md border-gray-300 py-3 px-4 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email address *
              </label>
              <div className="relative mt-1">
                <input
                  type="email"
                  name="email"
                  id="email"
                  defaultValue={actionData?.fields?.email}
                  autoComplete="email"
                  className={classNames(
                    actionData?.fieldErrors?.email
                      ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                      : '',
                    'block w-full rounded-md border-gray-300 py-3 px-4 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                  )}
                  required
                  aria-invalid={
                    Boolean(actionData?.fieldErrors?.email) || undefined
                  }
                  aria-describedby={
                    actionData?.fieldErrors?.email ? 'email-error' : undefined
                  }
                />
                {actionData?.fieldErrors?.email ? (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  </div>
                ) : null}
              </div>
              {actionData?.fieldErrors?.email ? (
                <p className="mt-2 text-sm text-red-600" id="email-error">
                  {actionData?.fieldErrors?.email}
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <input type="hidden" id="consent" name="consent" value={agreed} />
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Switch
                    checked={agreed}
                    onChange={setAgreed}
                    className={classNames(
                      agreed
                        ? tenant.color === 'teal'
                          ? 'bg-teal-600 focus:ring-teal-500'
                          : 'bg-blue-600 focus:ring-blue-500'
                        : 'bg-gray-200',
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 '
                    )}
                  >
                    <span className="sr-only">Agree to policies</span>
                    <span
                      aria-hidden="true"
                      className={classNames(
                        agreed ? 'translate-x-5' : 'translate-x-0',
                        'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                      )}
                    />
                  </Switch>
                </div>
                <div className="ml-3">
                  <p className="text-base text-gray-500">
                    By selecting this, you agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="font-medium text-gray-700 underline"
                    >
                      Terms and Conditions
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      className="font-medium text-gray-700 underline"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                className={classNames(
                  agreed
                    ? tenant.color === 'teal'
                      ? 'bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500'
                      : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                    : 'bg-gray-400 text-white hover:cursor-not-allowed',
                  'inline-flex w-full items-center justify-center rounded-md border border-transparent px-6 py-3 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2'
                )}
                disabled={!agreed}
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
